// 🖥 방 화면공유 (발표자 1명 → 시청자 N명, 데스크톱 전용) — 2026-08-12 사용자 요청.
//   1:1 보이스톡(call.ts / tpt_calls)과 완전히 분리: 별도 라우트(/api/tpt/share/*)와 별도 문서
//   (tpt_room_shares). 기존 통화 코드를 건드리지 않아 회귀 위험이 없다.
//   방 알림 카드는 기존 [TPT_CALL] 마커를 재사용한다(mode:'roomshare') — 사이드바 미리보기·알림 토스트·
//   번역 대상 제외 치환이 이미 마커 단위로 되어 있어 새 마커를 만들면 4곳을 또 손봐야 하기 때문.

export interface ShareViewer {
	username?: string;
	name?: string;
	/** full = 정원(MAX_VIEWERS) 초과로 발표자가 거절 — 시청자에게 안내 후 종료 */
	state?: 'waiting' | 'offered' | 'connected' | 'left' | 'full';
	offer?: string;
	answer?: string;
}

export interface ShareDoc {
	shareId: string;
	rid: string;
	state: 'live' | 'ended';
	host: { userId?: string; username: string; name?: string };
	viewers?: ShareViewer[];
}

function hdr(token: string, userId: string): Record<string, string> {
	return { 'Content-Type': 'application/json', 'X-Auth-Token': token, 'X-User-Id': userId };
}

async function post<T>(path: string, token: string, userId: string, body: Record<string, unknown>): Promise<T> {
	const r = await fetch(`/api/tpt/share/${path}`, { method: 'POST', headers: hdr(token, userId), body: JSON.stringify(body) });
	const j = await r.json().catch(() => ({}));
	if (!r.ok || j.error) throw new Error(j.error || `share/${path} ${r.status}`);
	return j as T;
}

/** 발표 시작 — 이미 다른 사람이 공유 중이면 { busy:true, host } 반환(에러 아님). */
export function shareStart(token: string, userId: string, rid: string, hostName?: string): Promise<{ shareId: string; busy?: boolean; host?: string }> {
	return post('start', token, userId, { rid, ...(hostName ? { hostName } : {}) });
}

export function shareJoin(token: string, userId: string, shareId: string, name?: string): Promise<{ ok: boolean; host?: boolean }> {
	return post('join', token, userId, { shareId, ...(name ? { name } : {}) });
}

/** 발표자 → 특정 시청자에게 offer 전달 */
export function shareOffer(token: string, userId: string, shareId: string, viewer: string, sdp: string): Promise<{ ok: boolean }> {
	return post('offer', token, userId, { shareId, viewer, sdp });
}

/** 시청자 → 발표자에게 answer 전달 */
export function shareAnswer(token: string, userId: string, shareId: string, sdp: string): Promise<{ ok: boolean }> {
	return post('answer', token, userId, { shareId, sdp });
}

/** 발표자 → 정원 초과 시청자를 'full' 로 표시 (시청자 쪽에서 안내 후 종료) */
export function shareDeny(token: string, userId: string, shareId: string, viewer: string): Promise<{ ok: boolean }> {
	return post('deny', token, userId, { shareId, viewer });
}

export function shareLeave(token: string, userId: string, shareId: string): Promise<{ ok: boolean }> {
	return post('leave', token, userId, { shareId });
}

export function shareStop(token: string, userId: string, shareId: string): Promise<{ ok: boolean }> {
	return post('stop', token, userId, { shareId });
}

/** 🖥 참여 카드 대리 게시/갱신 (2026-09-04) — 읽기전용 방에서 발표자가 직접 못 올릴 때 서버(관리자 명의). op=post → cardId, op=end → 갱신 */
export function shareCard(token: string, userId: string, shareId: string, marker: string, op: 'post' | 'end', cardId?: string): Promise<{ ok: boolean; cardId?: string }> {
	return post('card', token, userId, { shareId, marker, op, ...(cardId ? { cardId } : {}) });
}

export async function shareState(token: string, userId: string, shareId: string): Promise<ShareDoc | null> {
	const r = await fetch(`/api/tpt/share/state?shareId=${encodeURIComponent(shareId)}`, { headers: hdr(token, userId) });
	if (!r.ok) return null;
	const j = await r.json().catch(() => null);
	return j && j.shareId ? (j as ShareDoc) : null;
}
